"use client"
import dynamic from "next/dynamic"
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react"
import {
  datacenterScreenPositionsStore,
  type GlobeEarthHoverTreatment,
} from "../../../globe/datacenterScreenPositions"
import {
  HoverSwapFadeOverlay,
  hoverSwapHiddenAndUntargetableContentStyle,
  useHoverSwapSequentialFade,
} from "../hoverSwapFade"
import { useHoverSwapsDisabled } from "../hoverSwapsDisabled"
import {
  HudHoverRuleTreatmentContext,
  HudHoverTakeoverDetail,
  TAKEOVER_DETAIL_TOP_NUDGE_PX,
} from "../hudHoverDetailHeader"

// Which size visual the datacenter list renders with — one HUD tab each.
// Treatments live in GlobeHoverDatacenterList.tsx:
//   "bars"    — thin horizontal bar after the size, length ∝ share
//   "squares" — one treemap-echo square, area ∝ share
//   "dots"    — 1–5 dither-echo dots, count ∝ share
//   "rank"    — no glyph: the top sites carry size typographically
//   "heat"    — hairline rule under the whole row, length ∝ share
export type GlobeHoverListVariant = "bars" | "squares" | "dots" | "rank" | "heat"

// How the list lays its rows out — one HUD tab each (knobs in
// GLOBE_DC_LIST_LAYOUTS, GlobeHoverDatacenterList.tsx):
//   "capped-two"          — two columns, capped with "And N more" (tabs 1–5)
//   "one-column-all"      — every site, one column, no cap
//   "two-column-all-tight"— every site, two columns, no cap; the name/
//                           location spacing tightens so rows fit, long
//                           names ellipsize
export type GlobeHoverListLayout =
  | "capped-two"
  | "one-column-all"
  | "two-column-all-tight"

// How the hovered datacenter's row marks itself in the list — one HUD tab
// each (knobs in GLOBE_DC_ROW_HIGHLIGHT_STYLE, GlobeHoverDatacenterList.tsx):
//   "red-bold"    — the stock: red bold text, red size mark
//   "invert"      — the whole row fills red; text and mark go cream
//   "invert-fade" — invert, with the fill/colors FADING in and out
//                   (GLOBE_DC_ROW_HIGHLIGHT_FADE_MS) instead of snapping
//   "ladder-dot"  — the capability ladder's current-level language: a red
//                   filled dot leads the row (every row reserves the slot),
//                   text bold black
//   "rail"        — a thick red rule down the row's left edge, red bold text
//   "underline"   — a red rule under the row's full width, bold black text
//   "frame"       — a red rounded outline around the row, bold black text
//   "frame-no-bold" — the frame alone; the text keeps its regular weight
//   "frame-name-bold" — the frame, with only the site NAME bolding
//   "rail-slide"  — the rail as ONE element that slides between rows as the
//                   hover moves (GLOBE_DC_SLIDE_MARKER_STYLE), red bold text
//   "frame-slide" — the frame as ONE sliding element, only the NAME bolds
//   "rail-fade"   — the rail per-row, its red BLOOMING in fast on arrival
//                   and lingering out slow on leave, with distinct easings
//                   (GLOBE_DC_RAIL_FADE_STYLE) so the trail feels organic
//   The rail-anim family (GLOBE_DC_RAIL_ANIM_VARIANTS — fast, no-trail
//   takes on the rail, one tab each):
//   "rail-fade-fast"    — quick symmetric fades, no lingering
//   "rail-cut"          — the old row cuts out at once; the new one settles in
//   "rail-wipe"         — the rail wipes DOWN into place, old one wipes away
//   "rail-pop"          — the rail pops in with a slight overshoot
//   "rail-instant-sweep"— text/bar red is INSTANT; only the rail sweeps in
export type GlobeHoverListRowHighlight =
  | "red-bold"
  | "invert"
  | "invert-fade"
  | "ladder-dot"
  | "rail"
  | "underline"
  | "frame"
  | "frame-no-bold"
  | "frame-name-bold"
  | "rail-slide"
  | "frame-slide"
  | "rail-fade"
  | "rail-fade-fast"
  | "rail-cut"
  | "rail-wipe"
  | "rail-pop"
  | "rail-instant-sweep"

// Lazy: the list body drags the full datacenter timeline table with it (see
// the note in GlobeHoverDatacenterList.tsx).
const GlobeHoverDatacenterList = dynamic(
  () => import("./GlobeHoverDatacenterList").then(m => m.GlobeHoverDatacenterList),
  { ssr: false },
)

type GlobeHoverSwapContextValue = {
  variant?: GlobeHoverListVariant
  listLayout: GlobeHoverListLayout
  rowHighlight: GlobeHoverListRowHighlight
  isGlobeHovered: boolean
  setIsGlobeHovered: (hovered: boolean) => void
  // The datacenter under the pointer on the globe (the stack the ray hits,
  // else the nearest projected center within the hover radius — see the hit
  // test in DatacenterScreenPositionsPublisher) — its list row takes the
  // hover highlight.
  hoveredDcName: string | null
  setHoveredDcName: (name: string | null) => void
}

const GlobeHoverSwapContext = createContext<GlobeHoverSwapContextValue | null>(null)

// Debug handle: lets devtools (and the agent's preview probes) inspect what
// the globe scene is publishing without touching the canvas.
if (typeof window !== "undefined") {
  ;(window as unknown as Record<string, unknown>).__globeDcScreenStore =
    datacenterScreenPositionsStore
}

export const useGlobeHoverSwap = () => useContext(GlobeHoverSwapContext)

// Renders no DOM of its own. `variant` undefined keeps the whole feature
// inert (hitbox and target render children untouched) — the production tab
// stays byte-for-byte on its current behavior.
export const GlobeHoverSwap = ({
  variant,
  listLayout = "capped-two",
  rowHighlight = "red-bold",
  earthTreatment,
  children,
}: {
  variant?: GlobeHoverListVariant
  listLayout?: GlobeHoverListLayout
  rowHighlight?: GlobeHoverListRowHighlight
  // How the EARTH itself responds while hovered — rendered by the globe
  // scene, configured through the shared store (see GlobeEarthHoverTreatment
  // in datacenterScreenPositions.ts).
  earthTreatment?: GlobeEarthHoverTreatment
  children: ReactNode
}) => {
  const [isGlobeHovered, setIsGlobeHovered] = useState(false)
  const [hoveredDcName, setHoveredDcName] = useState<string | null>(null)
  // Warm the lazy list chunk so the first hover doesn't flash empty.
  useEffect(() => {
    if (variant !== undefined) void import("./GlobeHoverDatacenterList")
  }, [variant])
  // Tell the globe scene which earth treatment this tab wants, and that the
  // old floating datacenter tooltips should stay closed while the feature
  // owns the globe (the list takeover replaces them).
  useEffect(() => {
    datacenterScreenPositionsStore.earthHoverTreatment =
      variant !== undefined ? earthTreatment ?? null : null
    datacenterScreenPositionsStore.datacenterTooltipsSuppressed =
      variant !== undefined
    return () => {
      datacenterScreenPositionsStore.earthHoverTreatment = null
      datacenterScreenPositionsStore.datacenterTooltipsSuppressed = false
    }
  }, [variant, earthTreatment])
  // Mirror the hover state into the store for the scene's frame loops.
  useEffect(() => {
    datacenterScreenPositionsStore.earthHovered = isGlobeHovered
    return () => {
      datacenterScreenPositionsStore.earthHovered = false
    }
  }, [isGlobeHovered])
  const value = useMemo(
    () => ({ variant, listLayout, rowHighlight, isGlobeHovered, setIsGlobeHovered, hoveredDcName, setHoveredDcName }),
    [variant, listLayout, rowHighlight, isGlobeHovered, hoveredDcName],
  )
  // Hover is not an input on this surface (mobile dashboard) — provide
  // null so every consumer renders its base, unhovered content.
  const hoverSwapsDisabled = useHoverSwapsDisabled()
  return (
    <GlobeHoverSwapContext.Provider value={hoverSwapsDisabled ? null : value}>
      {children}
    </GlobeHoverSwapContext.Provider>
  )
}

// The hover gate for the OPAQUE EARTH. The globe canvas is a large
// transparent strip whose drawn pixels — the sphere plus the datacenter
// stacks poking past its silhouette — are the only place the globe hover may
// engage; everywhere else (the transparent corners, the graph's risk dither
// ring showing through around the earth) the graph keeps its own hover. DOM
// hit areas are rectangles, so the boundary can't be expressed with element
// geometry. Instead a document-level mousemove runs the scene's synchronous
// per-pixel hit test (a raycast against the sphere + datacenter layers —
// datacenterScreenPositionsStore.hitTest, registered by the globe scene) and
// toggles pointer-events on the CANVAS ITSELF to match: r3f gives the canvas
// inline pointer-events:auto (overriding the HUD wrapper's
// pointer-events-none — the canvas would otherwise swallow the pointer over
// the whole strip and dead-zone the graph hover under it), and the canvas
// sits above the graph's hover surface, so it already is the per-pixel
// shield we need. Over an opaque pixel it stays auto — blocking the graph
// hover and keeping the canvas's own interactions (drag-to-spin, stack
// highlight) exactly where the drawn globe is; over a transparent pixel it
// goes none, and the graph hover underneath works untouched. Renders
// nothing; mount as a child of HudPersistentGlobeAndGraph (next to
// CapabilityHoverBridge).
export const GlobeHoverPointerGate = () => {
  const swap = useGlobeHoverSwap()
  const enabled = Boolean(swap && swap.variant !== undefined)
  const setIsGlobeHovered = swap?.setIsGlobeHovered
  const setHoveredDcName = swap?.setHoveredDcName
  useEffect(() => {
    if (!enabled || !setIsGlobeHovered || !setHoveredDcName) return
    let lastClientX = Number.NaN
    let lastClientY = Number.NaN
    let gatedCanvas: HTMLCanvasElement | null = null
    const setCanvasPointerEvents = (value: "auto" | "none") => {
      const { canvasEl } = datacenterScreenPositionsStore
      if (gatedCanvas && gatedCanvas !== canvasEl) {
        // The canvas remounted (error boundary, settings change) — put the
        // old one back on r3f's default.
        gatedCanvas.style.pointerEvents = "auto"
      }
      gatedCanvas = canvasEl
      if (canvasEl) canvasEl.style.pointerEvents = value
    }
    const clear = () => {
      setCanvasPointerEvents("none")
      setIsGlobeHovered(false)
      setHoveredDcName(null)
    }
    const update = () => {
      const { canvasEl, hitTest } = datacenterScreenPositionsStore
      if (!canvasEl || !hitTest || Number.isNaN(lastClientX)) {
        clear()
        return
      }
      // A faded-out globe (galaxy hide, metric-details crossfade) has no
      // opaque pixels to hover.
      if (!(canvasEl.checkVisibility?.({ checkOpacity: true, checkVisibilityCSS: true }) ?? true)) {
        clear()
        return
      }
      const result = hitTest(lastClientX, lastClientY)
      setCanvasPointerEvents(result.overOpaqueGlobe ? "auto" : "none")
      setIsGlobeHovered(result.overOpaqueGlobe)
      setHoveredDcName(result.overOpaqueGlobe ? result.hoveredDcName : null)
    }
    const onMouseMove = (event: MouseEvent) => {
      lastClientX = event.clientX
      lastClientY = event.clientY
      update()
    }
    // The globe moves under a stationary pointer while the page scrolls
    // through camera keyframes — re-run the last pointer position.
    const onScroll = () => update()
    const onDocumentLeave = () => {
      lastClientX = Number.NaN
      lastClientY = Number.NaN
      clear()
    }
    document.addEventListener("mousemove", onMouseMove, { passive: true })
    window.addEventListener("scroll", onScroll, { passive: true, capture: true })
    document.documentElement.addEventListener("mouseleave", onDocumentLeave)
    return () => {
      document.removeEventListener("mousemove", onMouseMove)
      window.removeEventListener("scroll", onScroll, { capture: true })
      document.documentElement.removeEventListener("mouseleave", onDocumentLeave)
      setIsGlobeHovered(false)
      setHoveredDcName(null)
      // Hand the canvas back to r3f's default so the feature-off state is
      // byte-for-byte the old behavior.
      setCanvasPointerEvents("auto")
    }
  }, [enabled, setIsGlobeHovered, setHoveredDcName])
  return null
}

// Wraps the HUD's lower half: while the globe is hovered the children keep
// their layout footprint but go visibility-hidden, and the datacenter list
// paints over them. Pass-through when no variant is configured.
export const GlobeHoverSwapTarget = ({
  className,
  children,
}: {
  className?: string
  children: ReactNode
}) => {
  const swap = useGlobeHoverSwap()
  const sequentialFade = useHoverSwapSequentialFade()
  // Non-stock rule treatments (2026-07-11): the datacenter list gets NO
  // separator rule — like the capability takeover, it starts flush where the
  // covered HUD content starts.
  const stockRule = useContext(HudHoverRuleTreatmentContext) === "stock"
  const variant = swap?.variant
  const active = variant !== undefined && Boolean(swap?.isGlobeHovered)
  return (
    <div className={`relative ${className ?? ""}`}>
      <div style={hoverSwapHiddenAndUntargetableContentStyle(active, sequentialFade)}>
        {children}
      </div>
      {/* z-[17]: the datacenter list can run taller than the lower half and
          paint over the narration bar's slot (z-[16], GraphAudioControls) —
          the list covers the bar while it drops out, instead of the bar
          showing through on top of the list text. */}
      <HoverSwapFadeOverlay
        active={active}
        className="absolute top-0 left-0 right-0 z-[17]"
      >
        {active && variant !== undefined && (() => {
          const list = (
            <GlobeHoverDatacenterList
              variant={variant}
              layout={swap?.listLayout ?? "capped-two"}
              rowHighlight={swap?.rowHighlight ?? "red-bold"}
              hoveredDcName={swap?.hoveredDcName ?? null}
            />
          )
          return stockRule ? (
            <HudHoverTakeoverDetail>{list}</HudHoverTakeoverDetail>
          ) : (
            <div style={{ paddingTop: TAKEOVER_DETAIL_TOP_NUDGE_PX }}>{list}</div>
          )
        })()}
      </HoverSwapFadeOverlay>
    </div>
  )
}
